// Pooled camera-facing ribbons (Phase Rend slashes / blink tears / cut lines, Comet Dive fire trail and ground cracks, Force
// Shout speed lines). One dynamic mesh: a fixed pool of triangle strips, each SEG segments long. Per point the caller gives
// position, width and alpha; per strip a sheath colour and a white-hot core colour with its own half-width (crisp cel bands,
// the same look as the Finger Beam needle). No per-frame allocations: write the points into `pts`, then `commit()`.
import * as THREE from 'three/webgpu';
import { abs, attribute, mix, smoothstep, uv, vec3 } from 'three/tsl';
import { uploadPrefix } from './uploadTrim';

/** floats per point in `pts`: x, y, z, width (m), alpha (0..1) */
export const RS_STRIDE = 5;

export class RibbonStrips {
  readonly mesh: THREE.Mesh;
  /** scratch: write up to `seg + 1` points (RS_STRIDE floats each), then commit() */
  readonly pts: Float32Array;
  private geo = new THREE.BufferGeometry();
  private pos: Float32Array; private edge: Float32Array; private core: Float32Array;
  private n = 0;

  constructor(scene: THREE.Scene, readonly pool: number, readonly seg: number, additive = false, renderOrder = 5) {
    const vPer = (seg + 1) * 2, nV = pool * vPer;
    this.pts = new Float32Array((seg + 1) * RS_STRIDE);
    this.pos = new Float32Array(nV * 3); this.edge = new Float32Array(nV * 4); this.core = new Float32Array(nV * 4);
    const uvs = new Float32Array(nV * 2), idx = new Uint32Array(pool * seg * 6);
    for (let n = 0; n < pool; n++) {
      for (let i = 0; i <= seg; i++) { const v = n * vPer + i * 2; uvs[v * 2] = i / seg; uvs[v * 2 + 1] = 0; uvs[v * 2 + 2] = i / seg; uvs[v * 2 + 3] = 1; }
      for (let i = 0; i < seg; i++) { const v = n * vPer + i * 2, o = (n * seg + i) * 6; idx[o] = v; idx[o + 1] = v + 1; idx[o + 2] = v + 2; idx[o + 3] = v + 2; idx[o + 4] = v + 1; idx[o + 5] = v + 3; }
    }
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('redge', new THREE.BufferAttribute(this.edge, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('rcore', new THREE.BufferAttribute(this.core, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geo.setDrawRange(0, 0);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending });
    const e = attribute('redge', 'vec4'), c = attribute('rcore', 'vec4');
    const across = abs(uv().y.sub(0.5)).mul(2);
    const coreMask = smoothstep(c.w, c.w.mul(0.55), across);
    m.colorNode = mix(vec3(e.x, e.y, e.z), vec3(c.x, c.y, c.z), coreMask);
    m.opacityNode = smoothstep(1.0, 0.86, across).mul(e.w);
    this.mesh = new THREE.Mesh(this.geo, m); this.mesh.frustumCulled = false; this.mesh.renderOrder = renderOrder;
    scene.add(this.mesh);
  }

  begin(): void { this.n = 0; }

  /** Build one strip from the first `count` points in `pts` (2..seg+1; missing points repeat the last one at zero width).
   *  edge / core: linear colours (may exceed 1 for bloom); coreW: core half-width as a share of the ribbon (0..1). */
  commit(count: number, cam: THREE.Vector3, edge: THREE.Color, core: THREE.Color, coreW: number): void {
    if (this.n >= this.pool || count < 2) return;
    const S = this.seg, P = this.pos, E = this.edge, C = this.core, q = this.pts, v0 = this.n * (S + 1) * 2;
    for (let i = 0; i <= S; i++) {
      const j = Math.min(i, count - 1), o = j * RS_STRIDE;
      const px = q[o], py = q[o + 1], pz = q[o + 2], w = i < count ? q[o + 3] : 0, a = q[o + 4];
      // tangent from neighbours
      const ja = Math.max(0, j - 1), jb = Math.min(count - 1, j + 1);
      let tx = q[jb * RS_STRIDE] - q[ja * RS_STRIDE], ty = q[jb * RS_STRIDE + 1] - q[ja * RS_STRIDE + 1], tz = q[jb * RS_STRIDE + 2] - q[ja * RS_STRIDE + 2];
      if (tx * tx + ty * ty + tz * tz < 1e-10) { tx = 1; ty = 0; tz = 0; }
      const vx = cam.x - px, vy = cam.y - py, vz = cam.z - pz;
      let sx = ty * vz - tz * vy, sy = tz * vx - tx * vz, sz = tx * vy - ty * vx;
      const sl = Math.hypot(sx, sy, sz) || 1, hw = w * 0.5 / sl; sx *= hw; sy *= hw; sz *= hw;
      const k = (v0 + i * 2) * 3;
      P[k] = px - sx; P[k + 1] = py - sy; P[k + 2] = pz - sz; P[k + 3] = px + sx; P[k + 4] = py + sy; P[k + 5] = pz + sz;
      for (let s = 0; s < 2; s++) {
        const c4 = (v0 + i * 2 + s) * 4;
        E[c4] = edge.r; E[c4 + 1] = edge.g; E[c4 + 2] = edge.b; E[c4 + 3] = a;
        C[c4] = core.r; C[c4 + 1] = core.g; C[c4 + 2] = core.b; C[c4 + 3] = coreW;
      }
    }
    this.n++;
  }

  end(): void {
    this.geo.setDrawRange(0, this.n * this.seg * 6);
    // PERF-STRESS: upload only the strips in use
    uploadPrefix(this.n * (this.seg + 1) * 2, this.geo.attributes.position as THREE.BufferAttribute, this.geo.attributes.redge as THREE.BufferAttribute, this.geo.attributes.rcore as THREE.BufferAttribute);
  }

  clear(): void { this.n = 0; this.geo.setDrawRange(0, 0); }
}
